import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
const STORAGE_KEY = "stagvista.theme-mode";
const ThemeContext = React.createContext(null);
function resolveSystemPreference() {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function")
        return "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function resolve(mode) {
    return mode === "system" ? resolveSystemPreference() : mode;
}
function applyToDocument(resolvedMode) {
    document.documentElement.classList.toggle("dark", resolvedMode === "dark");
}
function readStoredMode() {
    if (typeof window === "undefined")
        return "dark";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" || stored === "system" ? stored : "dark";
}
/**
 * Toggles Tailwind's `dark:` variant (`darkMode: ["class"]`, every app's
 * own tailwind config) by adding/removing `.dark` on `<html>` — not on
 * some inner wrapper div, so it applies uniformly regardless of where a
 * consumer mounts this provider. Defaults to `"dark"`, not `"system"`:
 * this product is dark-first (docs/design.md §2), and the app shipped
 * hardcoded to dark before this provider existed — defaulting anyone
 * without a saved preference to their OS light mode would be a real,
 * surprising behavior change, not a neutral one.
 */
export function ThemeProvider({ children, defaultMode }) {
    const [mode, setModeState] = React.useState(() => defaultMode ?? readStoredMode());
    const [resolvedMode, setResolvedMode] = React.useState(() => resolve(mode));
    React.useEffect(() => {
        const next = resolve(mode);
        setResolvedMode(next);
        applyToDocument(next);
    }, [mode]);
    React.useEffect(() => {
        if (mode !== "system" || typeof window.matchMedia !== "function")
            return;
        const query = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = () => {
            const next = resolveSystemPreference();
            setResolvedMode(next);
            applyToDocument(next);
        };
        query.addEventListener("change", handleChange);
        return () => query.removeEventListener("change", handleChange);
    }, [mode]);
    const setMode = React.useCallback((next) => {
        setModeState(next);
        window.localStorage.setItem(STORAGE_KEY, next);
    }, []);
    const value = React.useMemo(() => ({ mode, resolvedMode, setMode }), [mode, resolvedMode, setMode]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
}
export function useTheme() {
    const ctx = React.useContext(ThemeContext);
    if (!ctx)
        throw new Error("useTheme must be used within a <ThemeProvider>");
    return ctx;
}
//# sourceMappingURL=theme-provider.js.map